"use client";

import type React from "react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  User,
  CreditCard,
  LogOut,
  Menu,
  X,
  Building2,
  RefreshCw,
  Shield,
  Package,
  ShoppingBagIcon,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";

const menuItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
  { icon: User, label: "Profile", path: "/profile" },
  { icon: CreditCard, label: "Plans", path: "/plans" },
  { icon: Package, label: "Plugins", path: "/plugins" },
  { icon: ShoppingBagIcon, label: "Orders", path: "/orders" },
  { icon: Shield, label: "Consents", path: "/consents" },
];

const Sidebar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { logout, invalidateAllTokens, user } = useAuthStore();

  const handleLogout = () => {
    logout();
  };

  const handleInvalidateAll = async () => {
    await invalidateAllTokens();
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white/10 backdrop-blur-sm text-white"
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <motion.div
        initial={{ x: -300 }}
        animate={{ x: isOpen || window.innerWidth >= 1024 ? 0 : -300 }}
        transition={{ duration: 0.3 }}
        className="fixed left-0 top-0 h-full w-64 banking-gradient shadow-2xl z-40 lg:translate-x-0"
      >
        <div className="p-6">
          <div className="flex items-center mb-8">
            <h1 className="text-xl font-bold text-white">Canara Bank</h1>
          </div>

          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive
                      ? "bg-white/20 text-white shadow-lg"
                      : "text-white/70 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Icon size={20} className="mr-3" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="text-white/70 text-sm mb-4">
              <p className="font-medium text-white">{user?.company_name}</p>
              <p className="truncate">{user?.email}</p>
            </div>

            <div className="space-y-2">
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-red-300 hover:bg-red-500/20 hover:text-red-200 rounded-lg transition-all duration-200"
              >
                <LogOut size={18} className="mr-3" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
